import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchOfferCalculations } from "../../../api/offer-calculation";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

function listCount(response, fallbackLength) {
  if (response?.totalCount != null) return Number(response.totalCount) || 0;
  if (response?.count != null) return Number(response.count) || 0;
  return fallbackLength;
}

/**
 * Picker for firm calculation templates (`OfferCalculation` with offer_id nil).
 *
 * @param {boolean} props.open
 * @param {Array} props.selectedCalculations
 * @param {Function} props.onChange — receives array of selected calculations
 * @param {Function} props.onClose
 * @param {boolean} props.multiple
 * @param {Array} props.filters
 * @param {string} [props.createTemplatePath] — empty-state link to create a template
 */
export default function ModernCalculationSelector({
  open = false,
  selectedCalculations = [],
  onChange,
  onClose,
  multiple = false,
  filters = [],
  showHeader = true,
  excludeCalculationId = null,
  createTemplatePath = "/dashboard/offers/calculations/new",
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [calculations, setCalculations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedCalculations.map((c) => c.id))
  );

  const perPage = 20;

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedCalculations.map((c) => c.id)));
    }
  }, [selectedCalculations, open]);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setCalculations([]);
    } else {
      setCalculations([]);
    }
  }, [open]);

  const searchFunction = useCallback(async (query, page, currentFilters, excludeId) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page: page,
        per_page: perPage,
        sortBy: "id",
        sort: "desc",
        fields: "id,title,description,calculation_type,total_amount,created_at",
        filters: [...currentFilters],
      };

      if (excludeId) {
        params.exclude_id = excludeId;
      }

      if (query !== undefined && query !== null) {
        params.query = query.trim() || undefined;
      }

      const response = await fetchOfferCalculations(params);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const count = listCount(response, rows.length);

      if (page === 1) {
        setCalculations(rows);
      } else {
        setCalculations((prev) => [...prev, ...rows]);
      }

      setHasMore(rows.length === perPage && page * perPage < count);
    } catch (err) {
      console.error("Error fetching calculations:", err);
      setError(t("offers.calculation.selector.error"));
      setCalculations([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  useEffect(() => {
    debouncedSearchRef.current.cancel();
    debouncedSearchRef.current = debounce(searchFunction, 300);
  }, [searchFunction]);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const filteredCalculations = useMemo(() => {
    if (!excludeCalculationId) return calculations;
    return calculations.filter((calc) => calc.id !== excludeCalculationId);
  }, [calculations, excludeCalculationId]);

  useEffect(() => {
    if (!open) {
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }

    setCurrentPage(1);
    debouncedSearchRef.current(searchQuery || "", 1, filtersRef.current, excludeCalculationId);

    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchQuery, open, excludeCalculationId]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery || "", nextPage, filtersRef.current, excludeCalculationId);
    }
  }, [isLoading, hasMore, currentPage, searchQuery, excludeCalculationId]);

  const toggleCalculation = useCallback(
    (calculation) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(calculation.id)) {
        newSelectedIds.delete(calculation.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(calculation.id);
      }

      setSelectedIds(newSelectedIds);

      const selectedFromCurrent = calculations.filter((c) => newSelectedIds.has(c.id));
      const selectedFromPrevious = selectedCalculations.filter(
        (c) =>
          newSelectedIds.has(c.id) &&
          !calculations.find((calc) => calc.id === c.id)
      );
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [selectedIds, calculations, selectedCalculations, multiple, onChange, onClose]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  const isSelected = useCallback(
    (calculationId) => selectedIds.has(calculationId),
    [selectedIds]
  );

  if (!open) return null;

  const searchInput = (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={t("offers.calculation.selector.search_placeholder")}
        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        autoFocus
      />
      {searchQuery ? (
        <button
          type="button"
          onClick={() => setSearchQuery("")}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple
                ? t("offers.calculation.selector.title_multiple")
                : t("offers.calculation.selector.title")}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title={t("offers.calculation.selector.clear")}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title={t("offers.calculation.selector.close")}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          {searchInput}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {t("offers.calculation.selector.selected_count", {
                count: selectedIds.size,
              })}
            </div>
          )}
        </div>
      )}

      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {searchInput}
        </div>
      )}

      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading && calculations.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">
                {t("offers.calculation.selector.loading")}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : calculations.length === 0 ? (
          <div className="flex items-center justify-center h-64 px-6">
            <div className="max-w-sm text-center">
              <p className="text-sm font-medium text-gray-700">
                {searchQuery
                  ? t("offers.calculation.selector.empty_search")
                  : t("offers.calculation.selector.empty")}
              </p>
              {!searchQuery ? (
                <>
                  <p className="mt-1 text-sm text-gray-500">
                    {t("offers.calculation.selector.empty_hint")}
                  </p>
                  {createTemplatePath ? (
                    <Link
                      to={createTemplatePath}
                      onClick={onClose}
                      className="mt-3 inline-flex items-center rounded-md bg-[#1463C7] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0B3A78]"
                    >
                      {t("offers.calculation.selector.create_template")}
                    </Link>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCalculations.map((calculation) => {
              const selected = isSelected(calculation.id);
              const typeLabel =
                typeof calculation.calculation_type === "string"
                  ? calculation.calculation_type
                  : calculation.calculation_type?.key ||
                    calculation.calculation_type?.name;

              return (
                <div
                  key={calculation.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggleCalculation(calculation) : undefined}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCalculation(calculation);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {calculation.title ||
                          calculation.displayTitle ||
                          t("offers.calculation.selector.untitled", {
                            id: calculation.id,
                          })}
                      </p>
                      {calculation.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {calculation.description}
                        </p>
                      )}
                      {calculation.total_amount !== undefined && (
                        <p className="text-xs text-gray-600 mt-1 font-semibold">
                          {t("offers.calculation.selector.total")}:{" "}
                          {new Intl.NumberFormat("no-NO", {
                            style: "currency",
                            currency: "NOK",
                          }).format(calculation.total_amount || 0)}
                        </p>
                      )}
                      {typeLabel ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t("offers.calculation.selector.type")}: {typeLabel}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !isLoading ? (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              type="button"
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {t("offers.calculation.selector.load_more")}
            </button>
          </div>
        ) : null}

        {isLoading && calculations.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>
    </div>
  );
}
